import React, { useEffect, useState } from 'react'
import TitleDivider from 'app/components/TitleDivider'
import {
  getEarningEstimates,
  getStakedValueFromTokens,
} from 'app/features/earn/earn.module'
import { enableStaking, stakeTokens, Status } from 'app/features/earn/earnSlice'
import { useAppDispatch, useAppSelector } from 'app/hooks/hooks'
import { RootState } from 'app/store'
import BN from 'bn.js'
import Web3 from 'web3'

type StakeHelper = {
  text: JSX.Element
}

const stakeHelperText: StakeHelper[] = [
  { text: <>Connect your wallet holding XOPAD to enable staking.</> },
  {
    text: (
      <>
        Enter the amount of XOPAD you wish to stake, or press the{' '}
        <span className="text-spring-green-600 text-bold">MAX</span> button to
        stake all of your XOPAD.
      </>
    ),
  },
  {
    text: (
      <>
        Press the Stake button. First approve the dApp to access your XOPAD.
        Then approve the staking transaction.
      </>
    ),
  },
  {
    text: (
      <>
        Once transaction is approved. Your XOPAD will be transfered to the
        staking contract to start earning BUSD rewards.
      </>
    ),
  },
]

type StakeContentProps = {
  xoBalance?: number
  apr?: number
  stakedValue?: number
  stakingStatus: Status
  web3?: Web3
  tokenPrice?: BN
}
const StakeContent = (props: StakeContentProps) => {
  const {
    xoBalance,
    apr = 0,
    stakedValue = 0,
    stakingStatus,
    web3,
    tokenPrice,
  } = props
  const dispatch = useAppDispatch()
  const [stakeInput, setStakeInput] = useState<number>()
  const [stakeInputValue, setStakeInputValue] = useState<number>(0)

  const getMaxTokens = () => {
    setStakeInput(Math.trunc(Number(xoBalance)))
  }

  const handleStakeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Math.trunc(Number(e.target.value))

    if (val > 0) {
      if (xoBalance && val >= xoBalance) {
        setStakeInput(xoBalance)
        return
      }

      setStakeInput(val)
      return
    }
    if (val < 0) {
      setStakeInput(0)
      return
    }
    setStakeInput(undefined)
  }

  const stake = () => {
    if (stakeInput) {
      dispatch(stakeTokens(stakeInput))
        .unwrap()
        .then(() => {
          setStakeInput(0)
          setStakeInputValue(0)
        })
        .catch((error) => {
          console.log(error)
          // TODO handle error
        })
    }
  }

  const {
    day: currentDay,
    month: currentMonth,
    year: currentYear,
  } = getEarningEstimates(stakedValue, apr)

  const {
    day: newDay,
    month: newMonth,
    year: newYear,
  } = getEarningEstimates(stakeInputValue + stakedValue, apr)

  useEffect(() => {
    const getAsValue = async () => {
      if (web3 && tokenPrice && stakeInput) {
        const value = await getStakedValueFromTokens(
          web3,
          stakeInput,
          tokenPrice
        )

        setStakeInputValue(value)
      }
    }
    getAsValue().catch((error) => {
      console.error(error)
      // TODO handle error
    })
  }, [stakeInput])

  return (
    <div>
      <label
        htmlFor="stakeAmount"
        className="block mb-2 text-sm font-medium text-gray-300"
      >
        Amount to stake
      </label>
      <div className="relative">
        <input
          type="number"
          id="stakeAmount"
          className="block w-full p-4 pl-10 text-sm text-white bg-gray-800 border border-gray-600 rounded-lg focus:ring-spring-green-600 focus:border-spring-green-600 "
          placeholder="0"
          required
          onChange={handleStakeInputChange}
          value={stakeInput}
        />
        <button
          type="submit"
          className="text-white absolute right-2.5 bottom-2.5 bg-spring-green-600 hover:bg-spring-green-700  focus:outline-none focus:ring-spring-green-300 focus:ring-0 font-medium rounded-lg text-sm px-4 py-2 "
          onClick={getMaxTokens}
        >
          Max
        </button>
      </div>
      <p id="helper-text-explanation" className="mt-2 text-sm text-gray-400">
        XOPAD Available:
        <span className="text-gray-300 pl-2">{xoBalance}</span>
      </p>
      <div className="">
        <div className="px-4 py-5 sm:px-6 text-white text-center">
          <div>
            <dt className="text-sm font-medium text-gray-400 truncate">
              Stake value
            </dt>
            <dd className="my-1 text-3xl font-semibold">${stakeInputValue}</dd>
          </div>
        </div>

        <h3 className="mt-3 text-sm leading-6 font-medium text-gray-400">
          Current Earnings
        </h3>
        <dl className="grid gap-5 grid-cols-3">
          <div className="px-1 py-1  rounded-lg overflow-hidden sm:p-2">
            <dt className="text-xs font-medium text-gray-400 truncate">Year</dt>
            <dd className="mt-1 text-lg sm:text-xl font-semibold text-gray-400">
              ${currentYear.toFixed(0)}
            </dd>
          </div>

          <div className="px-1 py-1  rounded-lg overflow-hidden sm:p-2">
            <dt className="text-xs font-medium text-gray-400 truncate">
              Month
            </dt>
            <dd className="mt-1 text-lg sm:text-xl font-semibold text-gray-400">
              ${currentMonth.toFixed(0)}
            </dd>
          </div>

          <div className="px-1 py-1  rounded-lg overflow-hidden sm:p-2">
            <dt className="text-xs font-medium text-gray-400 truncate">Day</dt>
            <dd className="mt-1 text-lg sm:text-xl font-semibold text-gray-400">
              ${currentDay.toFixed(2)}
            </dd>
          </div>
        </dl>
        <h3 className="text-lg leading-6 font-medium text-gray-400 mt-3">
          Estimated Earnings
        </h3>
        <dl className="grid gap-5 grid-cols-3">
          <div className="px-1 py-1  rounded-lg overflow-hidden sm:p-2">
            <dt className="text-sm font-medium text-gray-400 truncate">Year</dt>
            <dd className="mt-1 text-xl sm:text-3xl font-semibold text-white">
              ${newYear.toFixed(0)}
            </dd>
          </div>

          <div className="px-1 py-1  rounded-lg overflow-hidden sm:p-2">
            <dt className="text-sm font-medium text-gray-400 truncate">
              Month
            </dt>
            <dd className="mt-1 text-xl sm:text-3xl font-semibold text-white">
              ${newMonth.toFixed(0)}
            </dd>
          </div>

          <div className="px-1 py-1  rounded-lg overflow-hidden sm:p-2">
            <dt className="text-sm font-medium text-gray-400 truncate">Day</dt>
            <dd className="mt-1 text-xl sm:text-3xl font-semibold text-white">
              ${newDay.toFixed(2)}
            </dd>
          </div>
        </dl>
      </div>
      <div className="px-4 py-4 sm:px-6 text-center">
        <button
          type="button"
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full shadow-sm text-white bg-spring-green-600 hover:bg-spring-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-spring-green-400"
          onClick={() => {
            stake()
          }}
          disabled={stakingStatus === Status.loading}
        >
          {stakingStatus === Status.loading && (
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          )}
          Stake
        </button>
      </div>
      <div className="px-4 py-4 flex items-center text-center justify-center content-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-8 w-8 text-spring-green-600 mr-2 flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p id="helper-text-explanation" className="text-sm text-white">
          Minimum stake period of <span className="underline">30 days</span>
        </p>
      </div>
    </div>
  )
}
const Stake = () => {
  const { selectedAccount, xoBalance, web3 } = useAppSelector(
    (state: RootState) => state.web3
  )

  const { apr, stakedValue, stakingStatus, tokenPrice } = useAppSelector(
    (state: RootState) => state.earn
  )

  const dispatch = useAppDispatch()

  const onClickEnable = () => {
    dispatch(enableStaking())
  }

  return (
    <div className="grid grid-cols-1 gap-4 items-start lg:grid-cols-3 lg:gap-8">
      {/* Left column */}
      <div className="grid grid-cols-1 gap-4 lg:col-span-2">
        <section aria-labelledby="section-staking">
          <div className="overflow-hidden ">
            <div className="">
              <div className="mt-5">
                <div className="bg-ebony-clay-600 p-px overflow-hidden shadow-md rounded-lg bg-gradient-to-br from-spring-green-300 to-spring-green-600">
                  <div className="relative transition-all ease-in duration-75 bg-ebony-clay-600 rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                      <TitleDivider title="Stake" color="spring-green-600" />

                      {StakeContent({
                        xoBalance,
                        apr,
                        stakedValue,
                        stakingStatus,
                        web3,
                        tokenPrice,
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Right column */}
      <div className="grid grid-cols-1 gap-4">
        <section aria-labelledby="section-stake-help">
          <div className="overflow-hidden">
            <div className="">
              <div className="md:py-6">
                <h2 className="text-spring-green-600 text-lg font-medium mb-3">
                  How to stake
                </h2>
                {stakeHelperText.map((item, index) => (
                  <div>
                    <div className="flex relative pb-5">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full inline-flex justify-center text-white relative z-10">
                        {/* <svg
                          fill="none"
                          stroke="currentColor"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                        </svg> */}
                        {index + 1}
                      </div>
                      <div className="flex-grow ml-3">
                        <p className="leading text-gray-400">{item.text}</p>
                      </div>
                    </div>
                  </div>
                ))}
                <p className="text-white">
                  Re-staking XOPAD will reset the staking period timer.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default Stake
